import { type CountryCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type CalculateRequestOrderItem } from '@hooks-api/types/apiRequest'
import { type SubshipInfo } from '@hooks-api/types/dataModel/subship'
import { type Nullable } from '@hooks-api/types/nullable'
import * as cartService from '../core/cartService'
import { type WpCartValues, type AddToCartForbidden, type CartItem, type PartialCartItem, type PartialSpecificAddonCartItem, type PartialSpecificAddonCartItemWithAddonMainProductId } from '../types'
import { validateCartItem, validateSpecificAddonCartItem, validateSpecificAddonCartItems } from '../utils/validateCartItem'
import { defaultCartValues, useWpCartSelector, useWpCartSetter, useWpCartAllItems } from './useWpCartStore'

export const useWpCartService = (countryCode: CountryCode) => {
  const cartItems = useWpCartAllItems(countryCode)
  const cartState = useWpCartSelector(countryCode, state => state.cart)
  const setCartValues = useWpCartSetter(countryCode)

  const addCartItem = useCallbackEvent((item: PartialCartItem): boolean => {
    if (!validateCartItem(item)) return false
    setCartValues(state => ({
      ...state,
      cart: cartService.addCartItem(state.cart, item),
    }))
    return true
  })

  const addSpecificAddonItems = useCallbackEvent((items: PartialSpecificAddonCartItem[]) => {
    setCartValues((state) => {
      if (!validateSpecificAddonCartItems(items)) return state
      return {
        ...state,
        cart: cartService.addSpecificAddonItems(state.cart, items),
      }
    })
  })

  const deleteCartItem = useCallbackEvent((item: PartialCartItem) => {
    setCartValues((state) => {
      if (!validateCartItem(item)) return state
      return {
        ...state,
        cart: cartService.deleteCartItem(state.cart, item),
      }
    })
  })

  const deleteSpecificAddonItem = useCallbackEvent((item: PartialSpecificAddonCartItemWithAddonMainProductId) => {
    setCartValues((state) => {
      if (!validateSpecificAddonCartItem(item)) return state
      return {
        ...state,
        cart: cartService.deleteSpecificAddonItem(state.cart, item),
      }
    })
  })
  const deleteSpecificAddonItems = useCallbackEvent((items: PartialSpecificAddonCartItemWithAddonMainProductId[]) => {
    setCartValues((state) => {
      if (!validateSpecificAddonCartItems(items)) return state
      return {
        ...state,
        cart: cartService.deleteSpecificAddonItems(state.cart, items),
      }
    })
  })

  const replaceCartItem = useCallbackEvent((targetCartItemId: CartItem['cartItemId'], item: PartialCartItem) => {
    setCartValues((state) => {
      if (!validateCartItem(item)) return state
      return {
        ...state,
        cart: cartService.replaceCartItem(state.cart, targetCartItemId, item),
      }
    })
  })

  const upsertCartItemForUnderstocking = useCallbackEvent((cartItems: {
    cartItemId: CartItem['cartItemId']
    stock: CartItem['stock']
    quantity: CartItem['quantity']
    delivery_class: CartItem['delivery_class']
    subship_info?: {
      quantity_per_time: SubshipInfo['quantity_per_time']
    }
  }[]) => {
    setCartValues((state) => {
      return {
        ...state,
        cart: cartService.upsertCartItemForUnderstocking(state.cart, cartItems),
      }
    })
  })

  const upsertSpecificAddonItem = useCallbackEvent((item: PartialSpecificAddonCartItemWithAddonMainProductId) => {
    setCartValues((state) => {
      if (!validateSpecificAddonCartItem(item)) return state
      return {
        ...state,
        cart: cartService.upsertSpecificAddonItem(state.cart, item),
      }
    })
  })

  const upsertCartItem = useCallbackEvent((item: PartialCartItem) => {
    setCartValues((state) => {
      if (!validateCartItem(item)) return state
      return {
        ...state,
        cart: cartService.upsertCartItem(state.cart, item),
      }
    })
  })

  const overwriteCartItems = useCallbackEvent((items: PartialCartItem[]) => {
    setCartValues((state) => {
      const validItems = items.filter(item => validateCartItem(item))
      return {
        ...state,
        cart: {
          ...state.cart,
          items: validItems,
        },
      }
    })
  })

  /**
   * NOTE: 預設保留 billing 資訊
   * TODO: 這個 naming 有點不好，會讓人以為只有清空 cart，但實際上是整個 store，而 store 中還有很多資訊
   */
  const resetCartValues = useCallbackEvent((args: { excludes?: (keyof WpCartValues)[] } = {}) => {
    const { excludes = ['billing'] } = args
    setCartValues((state) => {
      const newState = { ...defaultCartValues }
      excludes.forEach((key) => {
        if (key in newState) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete newState[key]
        }
      })
      return {
        ...state,
        ...newState,
      }
    })
  })

  const syncCartStoreFromSession = useCallbackEvent((
    args: {
      cartValues: Nullable<WpCartValues>
      orderItems: Nullable<CalculateRequestOrderItem[]>
      callback?: () => void
    },
  ) => {
    const { cartValues, orderItems, callback } = args
    if (!cartService.shouldSyncCartStoreFromSession(cartValues?.cart.items)) {
      callback?.()
      return
    }

    const orderItemSkus = orderItems?.map(item => item.sku)
    setCartValues(state => cartService.syncWpCartStoreFromECommerceSession(state, cartValues, orderItemSkus), callback)
  })

  /**
   * SPEC:
   * 情境1  庫存變化警示 (購物車頁、結帳頁)
   * 情境2  車內品項不可同時結帳
   *   寄倉商品只能單獨結帳 (寄倉擋寄倉、寄倉直購互擋)
   *   公益商品只能單獨結帳 (公益商品運送類別為 donation)
   *   常溫冷凍不能同時結帳
   * 情境3  車內品項已超過限購數量
   *   訂單限購 (超過訂單限制可買數量)
   *   終生限購 (超過限制客人終身可買數量)
   * 情境4  客人不符合購買資格
   *   【僅官網】經銷身分試圖在官網下單
   *   【僅官網】非Admin試圖購買私密商品
   */
  const isCartItemAddForbidden = useCallbackEvent(({
    itemWantToAdd,
    currentQuantity = 0,
    reachQuantityLimit = false,
  }: {
    itemWantToAdd: PartialCartItem
    currentQuantity?: number
    reachQuantityLimit?: boolean
  }): AddToCartForbidden | false => {
    // 公益商品、常溫冷凍不能同時結帳
    const mixedDeliveryClassIsForbidden = !!itemWantToAdd.delivery_class && cartItems.some(item => item.delivery_class !== itemWantToAdd.delivery_class)
    // 如果購物車內已有寄倉商品，則不能加入其他商品
    const addSubshipIsForbidden = cartItems.some(item => item.subship_info) || (!!cartItems.length && !!itemWantToAdd.subship_info)
    // 如果是終身限購商品，則需阻擋數量大於限制的情況
    let reachPurchaseLimit = false
    if (itemWantToAdd.product_limited?.life_limit && itemWantToAdd.quantity) {
      const purchasedCount = itemWantToAdd.product_limited.purchased_count ?? 0
      reachPurchaseLimit = purchasedCount + currentQuantity + itemWantToAdd.quantity > itemWantToAdd.product_limited.life_limit
    }
    const isForbidden = mixedDeliveryClassIsForbidden || addSubshipIsForbidden || reachPurchaseLimit
    if (isForbidden) {
      return {
        mixedDeliveryClassIsForbidden,
        addSubshipIsForbidden,
        reachPurchaseLimit,
        reachQuantityLimit,
      }
    }
    return false
  })

  const getCartItemById = useCallbackEvent((cartItemId: CartItem['cartItemId']) => {
    return cartService.getCartItemById(cartState, cartItemId)
  })

  return {
    addCartItem,
    addSpecificAddonItems,
    deleteCartItem,
    upsertCartItem,
    resetCartValues,
    isCartItemAddForbidden,
    overwriteCartItems,
    syncCartStoreFromSession,
    getCartItemById,
    upsertCartItemForUnderstocking,
    upsertSpecificAddonItem,
    deleteSpecificAddonItem,
    deleteSpecificAddonItems,
    replaceCartItem,
  }
}
